import { useMemo, useCallback } from 'react'

import { useQuery, useQueryClient } from '@tanstack/react-query'

import type { TaxExemptionDocument as TaxDocReq } from '@b-stock/location-api-client'

import api from '@api/location'
import type { ExemptionRequirements } from '@helpers/politicalDivisions'
import { countryOutputToAugmentedTaxDocReq } from '@helpers/politicalDivisions'

const baseKey = 'taxExemptionRequirements'

export type AugmentedTaxDocReq = TaxDocReq & {
  isCountryLevel?: boolean
}

type UseTaxExemptionRequirementsArg = {
  country?: string
}

type UseRegionalTaxExemptionRequirementsArg = {
  country?: string
  region?: string
}

export const useTaxExemptionRequirements = ({
  country,
}: UseTaxExemptionRequirementsArg) =>
  useQuery({
    queryKey: [baseKey, country],
    queryFn: () =>
      country ? api.fetchNationalExemptionReqs(country) : undefined,
    enabled: !!country,
  })

export const useFetchRegionalTaxExemptionRequirements = () => {
  const queryClient = useQueryClient()

  return useCallback(
    async (
      payload: UseRegionalTaxExemptionRequirementsArg
    ): Promise<ExemptionRequirements> => {
      const nationalReqs = await queryClient.query({
        queryKey: [baseKey, payload.country],
        queryFn: () =>
          payload.country
            ? api.fetchNationalExemptionReqs(payload.country)
            : undefined,
      })

      return countryOutputToAugmentedTaxDocReq(nationalReqs, payload.region)
    },
    [queryClient]
  )
}

export const useRegionalTaxExemptionRequirements = (
  payload: UseRegionalTaxExemptionRequirementsArg
) => {
  const { country, region } = payload
  const queryResult = useTaxExemptionRequirements({ country })

  const data = useMemo<ExemptionRequirements>(
    () => countryOutputToAugmentedTaxDocReq(queryResult.data, region),
    [queryResult.data, region]
  )

  return {
    ...queryResult,
    data,
  }
}
